import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// Interfaces
export interface Bank {
  id: number;
  name: string;
  code: string;
  initial_amount?: number;
  credit_card?: boolean;
  pdf_password?: string;
  netbank_username?: string;
  netbank_password?: string;
  netbank_configured?: boolean;
  current_balance?: number;
}

export interface CollectionBankDeposit {
  id: number;
  collection_id?: number;
  deposited_on: string;
  received_per_gram_rate: number;
  received_per_gram_rate_display: string;
  receipt_url?: string | null;
  has_receipt?: boolean;
}

export interface Collection {
  id: number;
  name: string;
  description?: string;
  material: 'Gold' | 'Silver' | 'Platinum' | 'Diamond';
  material_display_name: string;
  weight: number;
  weight_display: string;
  category: string;
  purchase_amount?: number;
  purchase_amount_display: string;
  per_gram_rate?: number; // server still returns per_gram_rate key for compatibility
  per_gram_rate_display: string;
  calculated_per_gram_rate?: number;
  calculated_per_gram_rate_display: string;
  in_bank?: boolean;
  bank_deposits?: CollectionBankDeposit[];
  bank_deposits_count?: number;
  has_bank_deposits?: boolean;
  latest_bank_received_display?: string | null;
  latest_bank_deposited_on?: string | null;
  // Specific image types
  front_image_url?: string;
  back_image_url?: string;
  top_image_url?: string;
  bottom_image_url?: string;
  bill_image_url?: string;
  bill_images?: Array<{ id: number; url: string; filename: string }>;
  bill_images_count?: number;
  // Image status flags
  has_front_image: boolean;
  has_back_image: boolean;
  has_top_image: boolean;
  has_bottom_image: boolean;
  has_bill_image: boolean;
  has_bill_images?: boolean;
  has_image: boolean;
  has_bill: boolean;
  image_count: number;
  // Legacy fields for backward compatibility
  image?: string;
  bill_image?: string;
  image_url?: string;
  image_data?: string;
  bill_image_data?: string;
  created_at: string;
  updated_at: string;
}

export interface CollectionSummary {
  weight_by_material: Record<string, string>;
  weight_by_category: Record<string, string>;
  total_purchase_amount: number;
  total_purchase_amount_display: string;
  per_gram_rate_by_material: Record<string, string>;
  per_gram_rate_by_category: Record<string, string>;
}

export interface MarketRatesToday {
  city: string;
  gold_22k_per_gram: number | null;
  silver_per_gram: number | null;
  platinum_per_gram: number | null;
  gold_22k_per_gram_display: string;
  silver_per_gram_display: string;
  platinum_per_gram_display: string;
}

export interface Store {
  id: number;
  name: string;
  address?: string;
}

export interface Product {
  id: number;
  name: string;
  brand?: string;
  barcode?: string;
}

export interface BillItem {
  id: number;
  product_id?: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export interface Bill {
  id: number;
  store?: Store;
  total_amount: number;
  purchase_date: string;
  status: 'pending' | 'processing' | 'processed' | 'failed';
  image_url?: string;
  items_count: number;
  items: BillItem[];
  created_at: string;
  updated_at: string;
}

export interface PriceAnalysis {
  product: Product;
  store?: Store;
  period_days: number;
  statistics: {
    average_price: number;
    min_price: number;
    max_price: number;
    latest_price: number;
    price_count: number;
  };
  price_history: Array<{
    price: number;
    purchase_date: string;
    store_name?: string;
  }>;
}

export interface ProductPriceComparison {
  product: Product;
  period_days: number;
  store_comparison: Array<{
    store?: Store;
    average_price: number;
    min_price: number;
    max_price: number;
    latest_price: number;
    purchase_count: number;
  }>;
}

export interface PriceHistoryEntry {
  id: number;
  price: number;
  purchase_date: string;
  product: Product;
  store?: Store;
  bill_id?: number;
  created_at: string;
}

export interface PriceHistoryResponse {
  price_histories: PriceHistoryEntry[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
  };
  filters: {
    products: Array<{ id: number; name: string }>;
    stores: Array<{ id: number; name: string }>;
  };
}

export interface FolderItem {
  name: string;
  path: string;
  type: 'directory' | 'file';
  size: number;
  size_known?: boolean;
  modified_at: string;
  extension?: string | null;
  mime_type?: string | null;
}

export interface FolderStorage {
  root: string;
  total: number;
  used: number;
  free: number;
  percent_used: number;
}

export interface FolderListing {
  path: string;
  parent_path: string | null;
  items: FolderItem[];
  storage: FolderStorage;
}

// Create axios instance with timeout for uploads
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 300000, // 5 minutes timeout for large file uploads
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('Adding token to request:', config.url);
  } else {
    console.log('No token found for request:', config.url);
  }
  return config;
});

// Handle token expiration and network errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle network errors (no response from server)
    if (!error.response) {
      console.error('Network error:', {
        message: error.message,
        code: error.code,
        config: error.config,
        url: error.config?.url,
        baseURL: error.config?.baseURL
      });
      
      // Check if it's a timeout
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        error.networkError = 'Request timeout. Please check your connection and try again.';
      } else if (error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
        error.networkError = 'Network error. Please check your internet connection and try again.';
      } else if (error.code === 'ERR_INTERNET_DISCONNECTED') {
        error.networkError = 'No internet connection. Please check your network.';
      } else {
        error.networkError = 'Unable to reach server. Please check your connection and try again.';
      }
    }
    
    // Handle 401 unauthorized
    if (error.response?.status === 401) {
      console.log('401 error intercepted:', error.config.url);
      // Only redirect to login if we're not already on the login page
      if (window.location.pathname !== '/login') {
        console.log('Redirecting to login due to 401');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string) =>
    api.post('/auth/login', { email, password }),
  register: (name: string, email: string, password: string) =>
    api.post('/auth/register', { name, email, password }),
  validateToken: () => api.get('/auth/validate'),
};

// Banks API
export const banksAPI = {
  getAll: async () => {
    console.log('Calling banksAPI.getAll()');
    const response = await api.get('/banks');
    console.log('Banks API response:', response);
    return response;
  },
  create: (data: Partial<Bank>) => api.post('/banks', data),
  update: (id: number, data: Partial<Bank>) => api.put(`/banks/${id}`, data),
};

// Categories API
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  getById: (id: number) => api.get(`/categories/${id}`),
  create: (data: { name: string; color: string }) =>
    api.post('/categories', data),
  update: (id: number, data: { name: string; color: string }) =>
    api.put(`/categories/${id}`, data),
  delete: (id: number) => api.delete(`/categories/${id}`),
};

// Transactions API
export const transactionsAPI = {
  getAll: (params?: {
    type?: string;
    category_id?: number;
    bank_id?: number;
    start_date?: string;
    end_date?: string;
    min_amount?: number;
    max_amount?: number;
    search?: string;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    page?: number;
    per_page?: number;
  }) => api.get('/transactions', { params }),
  getById: (id: number) => api.get(`/transactions/${id}`),
  create: (data: {
    amount: number;
    description: string;
    date: string;
    transaction_type: 'income' | 'expense' | 'transfer';
    category_id: number;
    bank_id: number;
    to_bank_id?: number;
    to_bank_name?: string;
    reference_number?: string;
  }) => api.post('/transactions', data),
  update: (
    id: number,
    data: {
      amount: number;
      description: string;
      date: string;
      transaction_type: 'income' | 'expense' | 'transfer';
      category_id: number;
      bank_id: number;
      to_bank_id?: number;
      to_bank_name?: string;
      reference_number?: string;
    }
  ) => api.put(`/transactions/${id}`, data),
  delete: (id: number) => api.delete(`/transactions/${id}`),
  suggestCategories: (description: string) => api.get('/transactions/suggest_categories', { params: { description } }),
  getUnknownTransactions: (params?: { page?: number; per_page?: number }) => 
    api.get('/transactions/unknown_transactions', { params }),
  bulkUpdateCategory: (data: {
    transaction_ids: number[];
    category_id: number;
    keywords?: string[];
  }) => api.post('/transactions/bulk_update_category', data),
};

// Bank Statements API
export const bankStatementsAPI = {
  getAll: (params?: {
    page?: number;
    per_page?: number;
  }) => api.get('/bank_statements', { params }),
  getById: (id: number) => api.get(`/bank_statements/${id}`),
  create: (data: { file_name: string; bank_id: number }) =>
    api.post('/bank_statements', data),
  upload: (id: number, file: File, password?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (password) {
      formData.append('password', password);
    }
    return api.post(`/bank_statements/${id}/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  delete: (id: number) => api.delete(`/bank_statements/${id}`),
};

// Folders API (direct filesystem manager under FILE_MANAGER_ROOT)
export const foldersAPI = {
  list: (params?: {
    path?: string;
    search?: string;
    sort_by?: 'name' | 'size' | 'modified' | 'type';
    sort_order?: 'asc' | 'desc';
    show_hidden?: boolean;
  }) => api.get('/folders', { params }),
  createDirectory: (path: string, name: string) =>
    api.post('/folders/create_directory', { path, name }),
  upload: (path: string, files: File[]) => {
    const formData = new FormData();
    formData.append('path', path);
    files.forEach((file) => formData.append('files[]', file));
    return api.post('/folders/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 300000,
    });
  },
  download: (path: string) =>
    api.get('/folders/download', { params: { path }, responseType: 'blob' }),
  preview: (path: string) =>
    api.get('/folders/preview', { params: { path }, responseType: 'blob' }),
  thumbnail: (path: string, size = 320) =>
    api.get('/folders/thumbnail', { params: { path, size }, responseType: 'blob' }),
  delete: (path: string) => api.delete('/folders', { params: { path } }),
};

// Dashboard API
export const dashboardAPI = {
  getSummary: (params?: { year?: number; month?: number; start_date?: string; end_date?: string }) => 
    api.get('/dashboard/summary', { params }),
  getCharts: (params?: { year?: number; month?: number; start_date?: string; end_date?: string }) => 
    api.get('/dashboard/charts', { params }),
};

// Planned Expenses API
export const plannedExpensesAPI = {
  getAll: () => api.get('/planned_expenses'),
  getById: (id: number) => api.get(`/planned_expenses/${id}`),
  create: (data: {
    title: string;
    description?: string;
    amount: number;
    frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
    status: 'active' | 'paused' | 'completed' | 'cancelled';
    expense_type: 'loan_emi' | 'rent' | 'subscription' | 'insurance' | 'utility' | 'other';
    start_date: string;
    end_date: string;
    due_day: number;
    auto_deduct?: boolean;
    payment_method: 'upi' | 'neft' | 'rtgs' | 'imps' | 'auto_debit' | 'cheque' | 'cash' | 'credit_card' | 'debit_card' | 'other_method';
    reference_number?: string;
    category_id: number;
    bank_id?: number;
  }) => api.post('/planned_expenses', data),
  update: (id: number, data: any) => api.put(`/planned_expenses/${id}`, data),
  delete: (id: number) => api.delete(`/planned_expenses/${id}`),
  toggleStatus: (id: number) => api.post(`/planned_expenses/${id}/toggle_status`),
  getDashboardSummary: () => api.get('/planned_expenses/dashboard_summary'),
  getByType: () => api.get('/planned_expenses/by_type'),
  manualMatch: (transactionId: number, plannedExpenseId: number) => 
    api.post('/planned_expenses/manual_match', { transaction_id: transactionId, planned_expense_id: plannedExpenseId }),
};

// Transaction Approvals API
export const transactionApprovalsAPI = {
  getApprovals: () => api.get('/transaction_approvals'),
  approve: (id: number, notes?: string) => api.post(`/transaction_approvals/${id}/approve`, { notes }),
  reject: (id: number, notes?: string) => api.post(`/transaction_approvals/${id}/reject`, { notes }),
  markDuplicate: (id: number) => api.post(`/transaction_approvals/${id}/mark_duplicate`),
  bulkApprove: (transactionIds: number[], notes?: string) => 
    api.post('/transaction_approvals/bulk_approve', { transaction_ids: transactionIds, notes }),
  bulkReject: (transactionIds: number[], notes?: string) => 
    api.post('/transaction_approvals/bulk_reject', { transaction_ids: transactionIds, notes }),
  createAutoApprovalRule: (keyword: string, categoryId: number) => 
    api.post('/transaction_approvals/create_auto_approval_rule', { keyword, category_id: categoryId }),
  applyRulesToUnknown: () =>
    api.post('/transaction_approvals/apply_rules_to_unknown'),
  updateAutoApprovalRule: (ruleId: number, keyword: string, categoryId: number) => 
    api.post('/transaction_approvals/update_auto_approval_rule', { rule_id: ruleId, keyword, category_id: categoryId }),
  deleteAutoApprovalRule: (ruleId: number) => 
    api.delete('/transaction_approvals/delete_auto_approval_rule', { data: { rule_id: ruleId } }),
  assignCategoryToUnknown: (transactionIds: number[], categoryId: number, keywords?: string[]) => 
    api.post('/transaction_approvals/assign_category_to_unknown', { 
      transaction_ids: transactionIds, 
      category_id: categoryId, 
      keywords 
    }),
  updateTransactionCategory: (transactionId: number, categoryId: number) => 
    api.put(`/transaction_approvals/${transactionId}/update_category`, { category_id: categoryId }),
  deleteTransaction: (transactionId: number) => 
    api.delete(`/transaction_approvals/${transactionId}/delete_transaction`),
  bulkDelete: (transactionIds: number[]) => 
    api.post('/transaction_approvals/bulk_delete', { transaction_ids: transactionIds }),
};

// Failed Email Jobs API
export const failedEmailJobsAPI = {
  getAll: () => api.get('/failed_email_jobs'),
  getById: (id: number) => api.get(`/failed_email_jobs/${id}`),
  retry: (id: number) => api.post(`/failed_email_jobs/${id}/retry`),
  bulkRetry: (jobIds: number[]) => 
    api.post('/failed_email_jobs/bulk_retry', { job_ids: jobIds }),
};

// Collections API
export const collectionsAPI = {
  getAll: (params?: {
    material?: string;
    category?: string;
  }) => api.get('/collections', { params }),
  getById: (id: number) => api.get(`/collections/${id}`),
  create: (data: {
    name: string;
    description?: string;
    material: 'Gold' | 'Silver' | 'Platinum' | 'Diamond';
    weight: number;
    category: string;
    purchase_amount?: number;
    purchase_per_gram_rate?: number;
    in_bank?: boolean;
  }) => api.post('/collections', data),
  update: (id: number, data: Partial<Collection>) => api.put(`/collections/${id}`, data),
  delete: (id: number) => api.delete(`/collections/${id}`),
  uploadImage: (id: number, image: File, imageType: 'front' | 'back' | 'top' | 'bottom' | 'bill') => {
    const formData = new FormData();
    formData.append('image', image);
    formData.append('image_type', imageType);
    return api.post(`/collections/${id}/upload_image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  uploadBillImages: (id: number, images: File[]) => {
    const formData = new FormData();
    images.forEach((image) => {
      formData.append('images[]', image);
    });
    formData.append('image_type', 'bill');
    return api.post(`/collections/${id}/upload_image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  deleteBillImage: (id: number, billImageId: number) => {
    return api.delete(`/collections/${id}/delete_image`, {
      params: { image_type: 'bill', bill_image_id: billImageId }
    });
  },
  deleteImage: (id: number, imageType: 'front' | 'back' | 'top' | 'bottom' | 'bill') => {
    return api.delete(`/collections/${id}/delete_image`, {
      params: { image_type: imageType }
    });
  },
  createBankDeposit: (
    collectionId: number,
    payload: { deposited_on: string; received_per_gram_rate: number; receipt?: File | null }
  ) => {
    const fd = new FormData();
    fd.append('deposited_on', payload.deposited_on);
    fd.append('received_per_gram_rate', String(payload.received_per_gram_rate));
    if (payload.receipt) fd.append('receipt', payload.receipt);
    return api.post(`/collections/${collectionId}/bank_deposits`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateBankDeposit: (
    collectionId: number,
    depositId: number,
    payload: { deposited_on?: string; received_per_gram_rate?: number; receipt?: File | null }
  ) => {
    const fd = new FormData();
    if (payload.deposited_on != null) fd.append('deposited_on', payload.deposited_on);
    if (payload.received_per_gram_rate != null) {
      fd.append('received_per_gram_rate', String(payload.received_per_gram_rate));
    }
    if (payload.receipt) fd.append('receipt', payload.receipt);
    return api.put(`/collections/${collectionId}/bank_deposits/${depositId}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteBankDeposit: (collectionId: number, depositId: number) =>
    api.delete(`/collections/${collectionId}/bank_deposits/${depositId}`),
  uploadBankDepositReceipt: (collectionId: number, depositId: number, file: File) => {
    const fd = new FormData();
    fd.append('receipt', file);
    return api.post(`/collections/${collectionId}/bank_deposits/${depositId}/upload_receipt`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteBankDepositReceipt: (collectionId: number, depositId: number) =>
    api.delete(`/collections/${collectionId}/bank_deposits/${depositId}/purge_receipt`),
  getCategories: (material?: string) => api.get('/collections/get_categories', { 
    params: material ? { material } : {} 
  }),
  getSummary: () => api.get('/collections/summary'),
};

export const marketRatesAPI = {
  getToday: () => api.get('/market_rates/today'),
};

// Matching Configuration API
export interface MatchingConfiguration {
  id?: number;
  amount_tolerance_percentage: number;
  due_date_tolerance_days: number;
  high_priority_amount_tolerance: number;
  high_priority_date_tolerance: number;
  minimum_match_score: number;
  auto_match_enabled: boolean;
  require_manual_review: boolean;
  enable_frequency_bonus: boolean;
  enable_category_weighting: boolean;
  active: boolean;
}

export const matchingConfigurationAPI = {
  get: () => api.get('/matching_configuration'),
  create: (data: MatchingConfiguration) => api.post('/matching_configuration', { matching_configuration: data }),
  update: (data: Partial<MatchingConfiguration>) => api.put('/matching_configuration', { matching_configuration: data }),
  resetToDefaults: () => api.post('/matching_configuration/reset_to_defaults'),
};

// Planned Expense Matches API
export const plannedExpenseMatchesAPI = {
  getPendingMatches: () => api.get('/planned_expenses/pending_matches'),
  confirmMatch: (matchId: number) => api.post(`/planned_expenses/matches/${matchId}/confirm`),
  rejectMatch: (matchId: number) => api.post(`/planned_expenses/matches/${matchId}/reject`),
  getMatchHistory: () => api.get('/planned_expenses/match_history'),
};

// Bills API
export const billsAPI = {
  getAll: (params?: { store_id?: number; status?: string; from_date?: string; to_date?: string }) => 
    api.get('/bills', { params }),
  get: (id: number) => api.get(`/bills/${id}`),
  create: (data: Partial<Bill>) => api.post('/bills', { bill: data }),
  update: (id: number, data: Partial<Bill>) => api.put(`/bills/${id}`, { bill: data }),
  delete: (id: number) => api.delete(`/bills/${id}`),
  upload: (imageFile: File, purchaseDate?: string) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    if (purchaseDate) formData.append('purchase_date', purchaseDate);
    return api.post('/bills/upload', formData, {
      headers: { 
        'Content-Type': 'multipart/form-data',
      },
      timeout: 300000, // 5 minutes for large file uploads
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      },
    });
  },
  getPriceAnalysis: (productId: number, storeId?: number, days?: number) => 
    api.get('/bills/price_analysis', { params: { product_id: productId, store_id: storeId, days } }),
  getProductPriceComparison: (productId: number, days?: number) => 
    api.get('/bills/product_price_comparison', { params: { product_id: productId, days } }),
  getStoreProducts: (storeId: number, days?: number) => 
    api.get('/bills/store_products', { params: { store_id: storeId, days } }),
  reprocessOCR: (id: number) => api.post(`/bills/${id}/reprocess_ocr`),
  getPriceHistory: (params?: {
    product_id?: number;
    store_id?: number;
    days?: number;
    search?: string;
    min_price?: number;
    max_price?: number;
    start_date?: string;
    end_date?: string;
    page?: number;
    per_page?: number;
  }) => api.get('/bills/price_history', { params }),
};

export default api;
